import { useEffect, useMemo, useRef, useState } from 'react';
import { Outlet } from 'react-router-dom'
import { Avatar, Box, ButtonBase, Chip, Divider, Drawer, IconButton, InputBase, List, ListItem, ListItemText, ListItemIcon, Menu, MenuItem, ToggleButton, Typography, Tooltip } from '@mui/material';

import { ActiveNavLink } from 'components/ActiveNavLink';
import { SpaceDashboardRounded, ViewKanbanRounded, EditNoteRounded, PeopleAltRounded, WorkOutlineRounded, BadgeRounded, ApartmentRounded, NotificationsNoneRounded, FlagRounded, LogoutRounded, UnfoldMoreRounded, CheckRounded, StorageOutlined, CloudOutlined, SearchOutlined, Clear } from '@mui/icons-material';
import { SegmentedToggle } from 'components/layout/PageHeader';
import ChronosIcon from 'components/icons/ChronosIcon';
import { ApiEnv, canSwitchApiEnv, getApiEnv, switchApiEnv } from 'core/network/environment';
import { useGetRetoursCandidatsCount } from 'core/temp_workers/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-toastify';

import { SidebarListItemButton } from 'components/theme/buttons/sidebar.list.item.button';
import { LoadingBox } from 'components/loading.box';
import { useGetAgencies } from 'core/agencies/hooks';
import { useAuthDispatchContext } from 'core/auth/hooks';
import { useUserSettingsDispatchContext, useUserSettingsStateContext } from 'core/user.settings/hooks';
import { ReportModal } from 'core/report';
import { useReport } from 'core/report/context';
import { roleList } from 'pages/main/agents/constants';

/** Show a search field in the agency menu from this many agencies. */
const AGENCY_SEARCH_THRESHOLD = 3;
const AGENCY_MENU_MAX_HEIGHT = 420;

const normalize = (value: string) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

/** Dev-only: switch the dashboard between the local API and the production API (see core/network/environment). */
const ApiEnvSwitch = () => {
    const apiEnv = getApiEnv();
    const isProduction = apiEnv === 'production';

    const onChange = (_event: React.MouseEvent<HTMLElement>, value: ApiEnv | null) => {
        if (!value || value === apiEnv) return;
        if (value === 'production' && !window.confirm("Se connecter à l'API de production ?\n\nVous serez déconnecté et devrez vous identifier avec un compte de production. Toute modification effectuée sera réelle.")) return;
        switchApiEnv(value);
    };

    return (
        <Box sx={{
            mx: 0.5, mb: 1.5, p: 1.25, borderRadius: '12px',
            bgcolor: isProduction ? 'error.light' : 'background.default',
            border: '1px solid', borderColor: isProduction ? 'rgba(243,18,73,0.25)' : '#EDF0F3',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: isProduction ? 'error.main' : 'text.secondary' }}>
                    Données · dev only
                </Typography>
                {isProduction && (
                    <Box component='span' sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'error.main', boxShadow: '0 0 0 3px rgba(243,18,73,0.2)' }} />
                )}
            </Box>
            <SegmentedToggle exclusive fullWidth value={apiEnv} onChange={onChange} sx={{ height: 34, bgcolor: 'background.paper' }}>
                <ToggleButton value='local' sx={{ flex: 1, gap: 0.75 }}>
                    <StorageOutlined sx={{ fontSize: '1rem' }} /> Local
                </ToggleButton>
                <ToggleButton value='production' sx={{ flex: 1, gap: 0.75, '&.Mui-selected': { color: 'error.main' } }}>
                    <CloudOutlined sx={{ fontSize: '1rem' }} /> Prod
                </ToggleButton>
            </SegmentedToggle>
            {isProduction && (
                <Typography sx={{ mt: 1, fontSize: '0.6875rem', lineHeight: 1.35, color: 'error.main' }}>
                    Connecté à la production : les modifications sont réelles.
                </Typography>
            )}
        </Box>
    );
};

const SectionLabel = ({ children }: { children: React.ReactNode }) => (
    <Typography component='li' sx={{
        px: 1.25, pt: 2, pb: 0.75,
        fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase',
        color: 'text.secondary', listStyle: 'none',
    }}>
        {children}
    </Typography>
);

type NavItemProps = {
    to: string;
    end?: boolean;
    icon: React.ReactNode;
    label: string;
    count?: number;
};

const NavItem = ({ to, end, icon, label, count }: NavItemProps) => (
    <ListItem disablePadding sx={{ mb: 0.5 }}>
        <SidebarListItemButton component={ActiveNavLink} end={end} to={to}>
            <ListItemIcon>{icon}</ListItemIcon>
            <ListItemText primary={label} />
            {!!count && count > 0 && (
                <Box component='span' sx={{
                    ml: 1, px: 0.75, minWidth: 20, height: 20, borderRadius: '10px',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: '0.6875rem', fontWeight: 700, color: '#fff', bgcolor: 'error.main',
                }}>
                    {count > 99 ? '99+' : count}
                </Box>
            )}
        </SidebarListItemButton>
    </ListItem>
);

const DashboardLayout = () => {
    const queryClient = useQueryClient();
    const dispatch = useAuthDispatchContext();
    const setUserSettings = useUserSettingsDispatchContext();
    const { toggleModal: toggleReportModal } = useReport();
    const {agency_id, user: userData} = useUserSettingsStateContext();
    const {data: agencies, isLoading: isAgenciesLoading} = useGetAgencies({onError: () => toast.error('Une erreur est survenue.')});
    const retoursCandidatsCount = useGetRetoursCandidatsCount();

    const user = JSON.parse(userData || '{}');
    const isAdmin = user.role === 'admin';
    const [agencyMenuAnchor, setAgencyMenuAnchor] = useState<null | HTMLElement>(null);
    const [agencyQuery, setAgencyQuery] = useState('');
    const agencySearchRef = useRef<HTMLInputElement>(null);
    const closeAgencyMenu = () => { setAgencyMenuAnchor(null); setAgencyQuery(''); };
    const handleAgencyChange = (agencyId: number | string) => {
        closeAgencyMenu();
        setUserSettings('agency_id', agencyId);
        queryClient.invalidateQueries(['temp_workers']);
        queryClient.invalidateQueries(['offers']);
        queryClient.invalidateQueries(['agents']);
        queryClient.invalidateQueries(['agencies']);
        queryClient.invalidateQueries(['registrationStep']);
        queryClient.invalidateQueries(['candidates']);
        queryClient.invalidateQueries(['dashboard']);
        queryClient.invalidateQueries(['global_dashboard']);
        queryClient.invalidateQueries(['settings']);
        queryClient.invalidateQueries(['retours_candidats_count']);
    };

    const onLogout = () => {
        dispatch(null);
        queryClient.invalidateQueries();
    };

    const openAgencies: any[] = useMemo(() => (agencies || []).filter((agency: any) => !agency.closed), [agencies]);
    const closedAgencies: any[] = useMemo(() => (agencies || []).filter((agency: any) => agency.closed), [agencies]);

    const hasAgencySearch = (agencies?.length ?? 0) > AGENCY_SEARCH_THRESHOLD;
    const matchesQuery = (agency: any) => !agencyQuery || normalize(agency.name).includes(normalize(agencyQuery));
    const visibleOpenAgencies = openAgencies.filter(matchesQuery);
    const visibleClosedAgencies = closedAgencies.filter(matchesQuery);
    const noAgencyMatch = visibleOpenAgencies.length + visibleClosedAgencies.length === 0;

    useEffect(() => {
        if (agency_id === undefined && openAgencies.length > 0) setUserSettings('agency_id', openAgencies[0].id);
    }, [openAgencies, agency_id, setUserSettings]);

    const initials = [user.first_name, user.last_name].filter(Boolean).map((name: string) => name[0].toUpperCase()).join('') || '?';
    const currentAgency = agencies?.find((agency: any) => String(agency.id) === String(agency_id)) ?? openAgencies[0] ?? agencies?.[0];

    const renderAgencyMenuItem = (agency: any) => {
        const selected = String(agency.id) === String(currentAgency?.id);
        return (
            <MenuItem
                key={agency.id}
                selected={selected}
                onClick={() => handleAgencyChange(agency.id)}
                sx={{
                    gap: 1, px: 1.25, py: 0.875, borderRadius: '8px', fontSize: '0.8125rem', fontWeight: 500,
                    '&:hover, &.Mui-focusVisible': { bgcolor: '#F1F3F6' },
                    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': { bgcolor: 'secondary.light' },
                }}
            >
                <Typography noWrap sx={{ flex: 1, fontSize: 'inherit', fontWeight: selected ? 600 : 500, color: selected ? 'secondary.main' : (agency.closed ? 'text.secondary' : 'text.primary') }}>
                    {agency.name}
                </Typography>
                {agency.closed && <Chip label='Fermée' color='error' size='small' sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }} />}
                {selected && <CheckRounded sx={{ fontSize: '1rem', color: 'secondary.main' }} />}
            </MenuItem>
        );
    };

    return (
        <Box sx={{
            display: 'flex',
            maxWidth: '100vw',
            minHeight: '100vh',
            overflow: 'hidden',
        }}>
            <Drawer
                sx={{
                    '& .MuiDrawer-paper': {
                        display: 'flex',
                        flexDirection: 'column',
                        padding: '0 12px',
                        borderRight: '1px solid #EDF0F3',
                    },
                }}
            >
                {/* Brand */}
                <Box sx={{ display: 'flex', alignItems: 'center', height: 64, flexShrink: 0, px: 1 }}>
                    <Box
                        component='img'
                        alt='Chronos'
                        src={process.env.PUBLIC_URL + '/hlogo.svg'}
                        sx={{ height: 26 }}
                    />
                </Box>

                {/* Agency switcher */}
                <Box sx={{ px: 0.5, pb: 1, flexShrink: 0 }}>
                    {isAgenciesLoading && <LoadingBox />}
                    {agencies && agencies.length === 0 && (
                        <Box sx={{ px: 1.25, py: 1, borderRadius: '12px', bgcolor: '#FFF4E5', border: '1px solid #FFE0B2' }}>
                            <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: '#B26A00' }}>Aucune agence ouverte</Typography>
                            <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                Vous n'êtes rattaché à aucune agence ouverte. Contactez un administrateur.
                            </Typography>
                        </Box>
                    )}
                    {agencies && agencies.length > 0 && (
                        <>
                            <ButtonBase
                                onClick={(event) => agencies.length > 1 && setAgencyMenuAnchor(event.currentTarget)}
                                disableRipple={agencies.length === 1}
                                aria-haspopup={agencies.length > 1 ? 'menu' : undefined}
                                aria-expanded={agencyMenuAnchor ? 'true' : undefined}
                                sx={{
                                    width: '100%', justifyContent: 'flex-start', textAlign: 'left', gap: 1.25,
                                    px: 1.25, py: 1, borderRadius: '12px',
                                    bgcolor: 'background.default', border: '1px solid #EDF0F3',
                                    cursor: agencies.length > 1 ? 'pointer' : 'default',
                                    transition: 'border-color 150ms ease, box-shadow 150ms ease',
                                    ...(agencies.length > 1 && {
                                        '&:hover, &[aria-expanded="true"]': { borderColor: 'secondary.main', boxShadow: '0 0 0 3px rgba(46,107,235,0.12)' },
                                    }),
                                }}
                            >
                                <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <Typography sx={{ fontSize: '0.625rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'text.secondary', lineHeight: 1.2 }}>
                                        Agence
                                    </Typography>
                                    <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                                        {currentAgency?.name}
                                    </Typography>
                                </Box>
                                {currentAgency?.closed && <Chip label='Fermée' color='error' size='small' sx={{ height: 18, fontSize: '0.625rem', fontWeight: 600 }} />}
                                {agencies.length > 1 && <UnfoldMoreRounded sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />}
                            </ButtonBase>

                            <Menu
                                anchorEl={agencyMenuAnchor}
                                open={Boolean(agencyMenuAnchor)}
                                onClose={closeAgencyMenu}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                                // With a search field, keep focus in the input instead of the first item
                                autoFocus={!hasAgencySearch}
                                disableAutoFocusItem={hasAgencySearch}
                                // The modal focus trap wins over `autoFocus` on the input, so focus it once the menu is open
                                TransitionProps={{ onEntered: () => agencySearchRef.current?.focus() }}
                                PaperProps={{ sx: { mt: 0.75, width: agencyMenuAnchor?.offsetWidth, maxHeight: AGENCY_MENU_MAX_HEIGHT, borderRadius: '12px', boxShadow: 4, border: '1px solid #EDF0F3' } }}
                                MenuListProps={{ variant: hasAgencySearch ? 'menu' : 'selectedMenu', sx: { p: 0.75, pt: 0 } }}
                            >
                                {/* Sticky header: title + optional search */}
                                <Box sx={{ position: 'sticky', top: 0, zIndex: 1, bgcolor: 'background.paper', pt: 0.75, pb: 0.5, mb: 0.25, borderBottom: hasAgencySearch ? '1px solid #EDF0F3' : 'none' }}>
                                    <Typography sx={{ px: 1.25, pb: 0.5, fontSize: '0.625rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'text.secondary' }}>
                                        Changer d'agence{hasAgencySearch && <Box component='span' sx={{ ml: 0.5, color: '#B0B7C3' }}>· {agencies.length}</Box>}
                                    </Typography>
                                    {hasAgencySearch && (
                                        <Box sx={{
                                            mx: 0.5, mb: 0.5, height: 34, px: 1, display: 'flex', alignItems: 'center', gap: 0.75,
                                            borderRadius: '8px', bgcolor: 'background.default', border: '1px solid #EDF0F3',
                                            '&:focus-within': { borderColor: 'secondary.main', boxShadow: '0 0 0 3px rgba(46,107,235,0.12)', bgcolor: 'background.paper' },
                                        }}>
                                            <SearchOutlined sx={{ fontSize: '1.125rem', color: 'text.secondary' }} />
                                            <InputBase
                                                inputRef={agencySearchRef}
                                                value={agencyQuery}
                                                onChange={(event) => setAgencyQuery(event.target.value)}
                                                // Stop the Menu's type-ahead from hijacking letters typed in the field
                                                onKeyDown={(event) => { if (event.key !== 'ArrowDown' && event.key !== 'Escape' && event.key !== 'Tab') event.stopPropagation(); }}
                                                placeholder='Rechercher une agence'
                                                inputProps={{ 'aria-label': 'Rechercher une agence' }}
                                                sx={{ flex: 1, fontSize: '0.8125rem', '& input::placeholder': { color: 'text.secondary', opacity: 1 } }}
                                            />
                                            {agencyQuery && (
                                                <IconButton size='small' aria-label='Effacer' onClick={() => setAgencyQuery('')} sx={{ p: 0.25, color: 'text.secondary' }}>
                                                    <Clear sx={{ fontSize: '0.875rem' }} />
                                                </IconButton>
                                            )}
                                        </Box>
                                    )}
                                </Box>

                                {noAgencyMatch && (
                                    <Typography sx={{ px: 1.25, py: 2, textAlign: 'center', fontSize: '0.8125rem', color: 'text.secondary' }}>
                                        Aucune agence pour « {agencyQuery} »
                                    </Typography>
                                )}
                                {visibleOpenAgencies.map(renderAgencyMenuItem)}
                                {visibleClosedAgencies.length > 0 && (
                                    <>
                                        {visibleOpenAgencies.length > 0 && <Divider sx={{ my: 0.75, borderColor: '#EDF0F3' }} />}
                                        <Typography sx={{ px: 1.25, pt: 0.25, pb: 0.5, fontSize: '0.625rem', fontWeight: 600, letterSpacing: '-0.01em', textTransform: 'uppercase', color: 'text.secondary' }}>
                                            Agences fermées
                                        </Typography>
                                        {visibleClosedAgencies.map(renderAgencyMenuItem)}
                                    </>
                                )}
                            </Menu>
                        </>
                    )}
                </Box>

                {/* Navigation */}
                <List sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', py: 0 }}>
                    <SectionLabel>Pilotage</SectionLabel>
                    <NavItem to='/dashboard' end icon={<SpaceDashboardRounded />} label='Tableau de bord' />
                    {isAdmin && <NavItem to='/dashboard/chronos-global' end icon={<ChronosIcon />} label='Chronos Global' />}

                    <SectionLabel>Recrutement</SectionLabel>
                    <NavItem to='/dashboard/registrations' end icon={<ViewKanbanRounded />} label='Candidats' />
                    <NavItem to='/dashboard/retours-candidats' icon={<EditNoteRounded />} label='Retours candidats' count={retoursCandidatsCount} />
                    <NavItem to='/dashboard/candidates' icon={<PeopleAltRounded />} label='Intérimaires' />
                    <NavItem to='/dashboard/offers' icon={<WorkOutlineRounded />} label='Missions' />

                    {['admin', 'director', 'manager'].includes(user.role) && (
                        <>
                            <SectionLabel>Administration</SectionLabel>
                            <NavItem to='/dashboard/agents' icon={<BadgeRounded />} label='Agents' />
                            {['admin', 'director'].includes(user.role) && <NavItem to='/dashboard/agencies' icon={<ApartmentRounded />} label='Agences' />}
                        </>
                    )}
                </List>

                {/* Footer */}
                <Box sx={{ pb: 1.5, flexShrink: 0 }}>
                    <List sx={{ py: 0 }}>
                        <NavItem to='/dashboard/settings/notifications' icon={<NotificationsNoneRounded />} label='Notifications' />
                        <ListItem disablePadding sx={{ mb: 0.5 }}>
                            <ReportModal>
                                <SidebarListItemButton onClick={toggleReportModal}>
                                    <ListItemIcon><FlagRounded /></ListItemIcon>
                                    <ListItemText primary='Faire un retour' />
                                </SidebarListItemButton>
                            </ReportModal>
                        </ListItem>
                    </List>

                    <Divider sx={{ my: 1.5, borderColor: '#EDF0F3' }} />

                    {canSwitchApiEnv() && <ApiEnvSwitch />}

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, px: 1, py: 0.5 }}>
                        <Avatar sx={{ width: 36, height: 36, fontSize: '0.8125rem', fontWeight: 700, bgcolor: 'secondary.light', color: 'secondary.main' }}>
                            {initials}
                        </Avatar>
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography noWrap sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.primary', lineHeight: 1.3 }}>
                                {[user.first_name, user.last_name].filter(Boolean).join(' ') || user.email}
                            </Typography>
                            <Typography noWrap sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                                {roleList[user.role] ?? user.role}
                            </Typography>
                        </Box>
                        <Tooltip title='Se déconnecter' placement='top'>
                            <IconButton onClick={onLogout} size='small' aria-label='Se déconnecter' sx={{ color: 'text.secondary', '&:hover': { color: 'error.main', bgcolor: 'error.light' } }}>
                                <LogoutRounded sx={{ fontSize: '1.125rem' }} />
                            </IconButton>
                        </Tooltip>
                    </Box>
                </Box>
            </Drawer>
            <Box component="main" sx={{ flexGrow: 1, minWidth: 0, minHeight: '100vh', bgcolor: 'background.default', p: 0 }}>
                <Outlet />
            </Box>
        </Box>
    );
}

export default DashboardLayout;
